/**
 * HTTPクライアント (kyベース)
 *
 * kyライブラリを使用したシンプルで柔軟なHTTPクライアントを提供します
 */

import { getApiConfig } from '@src/config/api';
import ky, { type KyInstance, type Options as KyOptions } from 'ky';

/**
 * リクエストオプション
 */
interface RequestOptions {
  params?: Record<string, string | number>;
  query?: Record<string, string | number | boolean>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/**
 * APIエラークラス
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body?: unknown,
    message?: string
  ) {
    super(message || `API Error: ${status} ${statusText}`);
    this.name = 'ApiError';
  }
}

/**
 * HTTPクライアントの設定オプション
 */
interface HttpClientOptions {
  /** ベースURL（必ずプロトコル http:// または https:// を含めてください） */
  baseUrl?: string;
  headers?: Record<string, string>;
  timeout?: number;
}

/**
 * kyベースのHTTPクライアント
 */
export class HttpClient {
  private client: KyInstance;

  constructor(options: HttpClientOptions = {}) {
    const apiConfig = getApiConfig();
    const baseUrl = (options.baseUrl || apiConfig.baseUrl).trim();

    // URLが有効かチェック（プロトコルが必要）
    if (baseUrl && !baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
      const error = new Error(
        `HTTPクライアントの初期化エラー: baseURL "${baseUrl}" にプロトコル（http://またはhttps://）が必要です。`
      );
      console.error(error.message);
      throw error;
    }

    this.client = ky.create({
      prefixUrl: baseUrl,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      timeout: options.timeout || apiConfig.timeout,
    });
  }

  /**
   * パスパラメータとクエリパラメータを処理
   */
  private processUrl(path: string, options?: RequestOptions): string {
    let url = path;

    // prefixUrlを使用する場合、先頭のスラッシュを削除
    if (url.startsWith('/')) {
      url = url.slice(1);
    }

    // パスパラメータの置換
    if (options?.params) {
      for (const [key, value] of Object.entries(options.params)) {
        url = url.replace(`{${key}}`, String(value));
      }
    }

    return url;
  }

  /**
   * kyのオプションに変換
   */
  private toKyOptions(options?: RequestOptions): KyOptions {
    const kyOptions: KyOptions = {};

    if (options?.query) {
      kyOptions.searchParams = options.query as Record<string, string>;
    }

    if (options?.headers) {
      kyOptions.headers = options.headers;
    }

    if (options?.signal) {
      (kyOptions as KyOptions & { signal?: AbortSignal }).signal = options.signal;
    }

    return kyOptions;
  }

  private async requestJson<T>(
    method: 'get' | 'post' | 'put' | 'patch' | 'delete',
    path: string,
    body?: unknown,
    options?: RequestOptions,
    acceptHeader: string = 'application/json,application/pdf,text/plain,*/*'
  ): Promise<T> {
    try {
      const url = this.processUrl(path, options);
      const kyOptions = this.toKyOptions(options);

      const response = await this.client[method](url, {
        ...kyOptions,
        ...(body !== undefined ? { json: body } : {}),
        headers: {
          Accept: acceptHeader,
          ...kyOptions.headers,
        },
      });

      return await response.json<T>();
    } catch (error: any) {
      const status = error?.response?.status ?? error?.status;
      const statusText = error?.response?.statusText ?? error?.statusText;
      let parsedBody: unknown;
      try {
        parsedBody = await error?.response?.json();
      } catch {
        try {
          parsedBody = await error?.response?.text();
        } catch {
          parsedBody = undefined;
        }
      }
      throw new ApiError(status ?? 0, statusText ?? 'Unknown error', parsedBody, error?.message);
    }
  }

  private async requestBlob(
    method: 'get' | 'post',
    path: string,
    body?: unknown,
    options?: RequestOptions,
    acceptHeader = 'application/pdf,application/octet-stream,*/*'
  ): Promise<Blob> {
    const url = this.processUrl(path, options);
    const kyOptions = this.toKyOptions(options);

    const response = await this.client[method](url, {
      ...kyOptions,
      ...(body !== undefined ? { json: body } : {}),
      headers: {
        Accept: acceptHeader,
        ...kyOptions.headers,
      },
    });

    return await response.blob();
  }

  /**
   * GETリクエスト
   */
  async get<T = unknown>(path: string, options?: RequestOptions): Promise<T> {
    return this.requestJson('get', path, undefined, options);
  }

  /**
   * POSTリクエスト
   */
  async post<T = unknown>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.requestJson('post', path, body, options);
  }

  /**
   * PUTリクエスト
   */
  async put<T = unknown>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.requestJson('put', path, body, options);
  }

  /**
   * DELETEリクエスト
   */
  async delete<T = unknown>(path: string, options?: RequestOptions): Promise<T> {
    return this.requestJson('delete', path, undefined, options);
  }

  /**
   * PATCHリクエスト
   */
  async patch<T = unknown>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.requestJson('patch', path, body, options);
  }

  /**
   * FormDataを使ったPOSTリクエスト
   */
  async postFormData<T = unknown>(
    path: string,
    formData: FormData,
    options?: RequestOptions
  ): Promise<T> {
    const url = this.processUrl(path, options);
    const kyOptions = this.toKyOptions(options);

    const response = await this.client.post(url, {
      ...kyOptions,
      headers: {
        ...kyOptions.headers,
        'Content-Type': undefined,
      },
      body: formData,
    });

    return await response.json<T>();
  }

  /**
   * FormDataを使ったPUTリクエスト
   */
  async putFormData<T = unknown>(
    path: string,
    formData: FormData,
    options?: RequestOptions
  ): Promise<T> {
    const url = this.processUrl(path, options);
    const kyOptions = this.toKyOptions(options);

    const response = await this.client.put(url, {
      ...kyOptions,
      headers: {
        ...kyOptions.headers,
        'Content-Type': undefined,
      },
      body: formData,
    });

    return await response.json<T>();
  }

  /**
   * Blobを取得するGETリクエスト
   * PDFファイルなどのバイナリデータをダウンロードする際に使用
   */
  async getBlob(path: string, options?: RequestOptions): Promise<Blob> {
    return this.requestBlob('get', path, undefined, options);
  }

  /**
   * Blobを取得するPOSTリクエスト
   * PDF生成などで、POSTで直接Blobを返すAPIに使用
   */
  async postBlob(path: string, body?: unknown, options?: RequestOptions): Promise<Blob> {
    return this.requestBlob('post', path, body, options);
  }
}

/**
 * デフォルトクライアントインスタンス
 */
export const httpClient = new HttpClient();

/**
 * カスタム設定でクライアントを作成
 */
export function createHttpClient(options: HttpClientOptions): HttpClient {
  return new HttpClient(options);
}
